import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Source } from '../lib/ai';
import type { WorkspaceContextPayload, WorkspaceScreen } from '../lib/workspaceContext';
import type { IntelligenceDiagnostics } from '../lib/intelligenceApi';
import type { SystemTemplateDoc } from '../lib/systemTemplates';

import type { LexCounselDashboardBrief } from '../lib/lexCounselDashboardBrief';
import type { FilingReviewFinding, FilingReviewReport } from '../lib/filingReviewTypes';

export type LexCounselMode = 'draft' | 'ask' | 'research' | 'case';
export type EditorCounselMode = 'ask' | 'draft' | 'review';
export type DraftChangeScope = 'paragraph' | 'document' | 'selection' | 'insert' | 'new_tab';
export type DraftEditorPresentation = 'sidePanel' | 'inline';
export type DraftChangeStatus = 'pending' | 'accepted' | 'rejected' | 'stale' | 'failed';
export type DraftChangeRisk = 'standard' | 'high';

export interface PendingDraftChange {
  id: string;
  scope: DraftChangeScope;
  /** Draft document tab where this suggestion belongs. */
  tabId?: string;
  paragraphId?: string;
  selectionFrom?: number;
  selectionTo?: number;
  originalHtml: string;
  proposedHtml: string;
  applied?: boolean;
  messageIndex?: number;
  /** Plain-language explanation of why this proposed edit was made. */
  reason?: string;
  /** Short bullets summarizing what the proposal contains or changes. */
  keyPoints?: string[];
  /** True after the proposal was shown in the editor (document scope preview). */
  previewInEditor?: boolean;
  /** High-impact legal details require deliberate confirmation before bulk application. */
  risk?: DraftChangeRisk;
  status?: DraftChangeStatus;
  /** Original document revision used to generate this proposal. */
  documentFingerprint?: string;
  /** Set when a selection-scoped request fell back to paragraph replacement. */
  scopeFallbackNotice?: string;
}

export interface DraftChangeSet {
  id: string;
  prompt: string;
  summary: string;
  tabId?: string;
  status: 'reviewing' | 'completed' | 'clarification_required' | 'failed';
  changes: PendingDraftChange[];
  selectedChangeId?: string;
}

export interface LexCounselMessage {
  role: 'user' | 'ai';
  text: string;
  sources?: Source[];
  streaming?: boolean;
  thinkingStatus?: string;
  command?: string;
  structured?: Record<string, unknown>;
  diagnostics?: IntelligenceDiagnostics;
  draftChange?: PendingDraftChange;
  /** Shown above AI reply when mode was auto-switched for this turn. */
  modeSwitchNotice?: string;
  /** Secondary line under the thinking shimmer (e.g. document streaming). */
  activityNote?: string;
}

export interface LexCounselWorkspaceSnapshot {
  screen: WorkspaceScreen;
  workspaceId?: string;
  caseId?: string;
  caseTitle?: string;
  draftId?: string;
  draftTitle?: string;
  draftText?: string;
  selectedText?: string;
  selectedHtml?: string;
  selectionFrom?: number;
  selectionTo?: number;
  proceedings?: unknown[];
  caseData?: Record<string, unknown> | null;
  nextHearingDate?: string | null;
  researchSessionId?: string | null;
  workspaceContext?: WorkspaceContextPayload;
  draftTabs?: unknown[];
  activeTabId?: string;
  dashboardBrief?: LexCounselDashboardBrief;
  targetedParagraphId?: string | null;
  targetParagraphHtml?: string | null;
  formattingSystemTemplates?: SystemTemplateDoc[];
  formattingWorkspaceTemplates?: SystemTemplateDoc[];
  formattingUserData?: unknown;
}

export interface LexCounselConversationMeta {
  id: string;
  title: string;
  updatedAt: string;
  messageCount: number;
}

export type CounselPanelTheme = 'light' | 'dark';

interface LexCounselState {
  isOpen: boolean;
  panelTab: 'chat' | 'context' | 'timeline';
  panelTheme: CounselPanelTheme;
  activeMode: LexCounselMode;
  editorCounselMode: EditorCounselMode;
  targetedParagraphId: string | null;
  activePendingChange: PendingDraftChange | null;
  activeDraftChangeSet: DraftChangeSet | null;
  draftEditorPresentation: DraftEditorPresentation;
  workspace: LexCounselWorkspaceSnapshot;
  messages: LexCounselMessage[];
  conversations: LexCounselConversationMeta[];
  currentConversationId: string | null;
  showConversationList: boolean;
  isThinking: boolean;
  inputDraft: string;
  lastDiagnostics: IntelligenceDiagnostics | null;
  cachedHearingBrief: Record<string, unknown> | null;
  cachedResearchSessionId: string | null;
  warmupStatus: 'idle' | 'warming' | 'ready' | 'error';
  filingReviewReport: FilingReviewReport | null;
  filingReviewLoading: boolean;
  filingReviewError: string | null;
  activeReviewFindingId: string | null;
  reviewHighlights: FilingReviewFinding[];

  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  setActiveMode: (mode: LexCounselMode) => void;
  setEditorCounselMode: (mode: EditorCounselMode) => void;
  setTargetedParagraphId: (id: string | null) => void;
  setActivePendingChange: (change: PendingDraftChange | null) => void;
  setActiveDraftChangeSet: (changeSet: DraftChangeSet | null) => void;
  updateDraftChangeStatus: (id: string, status: DraftChangeStatus) => void;
  clearPendingChange: () => void;
  setDraftEditorPresentation: (presentation: DraftEditorPresentation) => void;
  setPanelTab: (tab: 'chat' | 'context' | 'timeline') => void;
  setPanelTheme: (theme: CounselPanelTheme) => void;
  setWorkspaceSnapshot: (snapshot: Partial<LexCounselWorkspaceSnapshot>) => void;
  setMessages: (messages: LexCounselMessage[] | ((prev: LexCounselMessage[]) => LexCounselMessage[])) => void;
  setConversations: (conversations: LexCounselConversationMeta[]) => void;
  setCurrentConversationId: (id: string | null) => void;
  setShowConversationList: (show: boolean) => void;
  setIsThinking: (thinking: boolean) => void;
  setInputDraft: (text: string) => void;
  setLastDiagnostics: (d: IntelligenceDiagnostics | null) => void;
  setCachedHearingBrief: (brief: Record<string, unknown> | null) => void;
  setCachedResearchSessionId: (id: string | null) => void;
  setWarmupStatus: (status: 'idle' | 'warming' | 'ready' | 'error') => void;
  setFilingReviewReport: (report: FilingReviewReport | null) => void;
  setFilingReviewLoading: (loading: boolean) => void;
  setFilingReviewError: (error: string | null) => void;
  setActiveReviewFindingId: (id: string | null) => void;
  setReviewHighlights: (findings: FilingReviewFinding[]) => void;
  clearFilingReview: () => void;
  startNewConversation: () => void;
}

export const useLexCounselStore = create<LexCounselState>()(
  persist(
    (set) => ({
      isOpen: false,
      panelTab: 'chat',
      panelTheme: 'light',
      activeMode: 'ask',
      editorCounselMode: 'draft',
      targetedParagraphId: null,
      activePendingChange: null,
      activeDraftChangeSet: null,
      draftEditorPresentation: 'sidePanel',
      workspace: { screen: 'dashboard' },
      messages: [],
      conversations: [],
      currentConversationId: null,
      showConversationList: false,
      isThinking: false,
      inputDraft: '',
      lastDiagnostics: null,
      cachedHearingBrief: null,
      cachedResearchSessionId: null,
      warmupStatus: 'idle',
      filingReviewReport: null,
      filingReviewLoading: false,
      filingReviewError: null,
      activeReviewFindingId: null,
      reviewHighlights: [],

      openPanel: () => set({ isOpen: true }),
      closePanel: () => set({ isOpen: false, showConversationList: false }),
      togglePanel: () => set((s) => ({ isOpen: !s.isOpen })),
      setActiveMode: (activeMode) => set({ activeMode }),
      setEditorCounselMode: (editorCounselMode) => set({ editorCounselMode }),
      setTargetedParagraphId: (targetedParagraphId) => set({ targetedParagraphId }),
      setActivePendingChange: (activePendingChange) => set({ activePendingChange }),
      setActiveDraftChangeSet: (activeDraftChangeSet) =>
        set({
          activeDraftChangeSet,
          activePendingChange:
            activeDraftChangeSet?.changes.find((change) => change.status === 'pending') ?? null,
        }),
      updateDraftChangeStatus: (id, status) =>
        set((state) => {
          const changeSet = state.activeDraftChangeSet;
          if (!changeSet) return state;
          const changes = changeSet.changes.map((change) =>
            change.id === id
              ? { ...change, status, applied: status === 'accepted' || change.applied }
              : change,
          );
          const nextPending = changes.find((change) => change.status === 'pending') ?? null;
          return {
            activeDraftChangeSet: {
              ...changeSet,
              changes,
              selectedChangeId: nextPending?.id,
              status: nextPending ? 'reviewing' : 'completed',
            },
            activePendingChange: nextPending,
          };
        }),
      clearPendingChange: () => set({ activePendingChange: null }),
      setDraftEditorPresentation: (draftEditorPresentation) => set({ draftEditorPresentation }),
      setPanelTab: (panelTab) => set({ panelTab }),
      setPanelTheme: (panelTheme) => set({ panelTheme }),
      setWorkspaceSnapshot: (snapshot) =>
        set((s) => ({
          workspace: { ...s.workspace, ...snapshot },
        })),
      setMessages: (messages) =>
        set((s) => ({
          messages: typeof messages === 'function' ? messages(s.messages) : messages,
        })),
      setConversations: (conversations) => set({ conversations }),
      setCurrentConversationId: (currentConversationId) => set({ currentConversationId }),
      setShowConversationList: (showConversationList) => set({ showConversationList }),
      setIsThinking: (isThinking) => set({ isThinking }),
      setInputDraft: (inputDraft) => set({ inputDraft }),
      setLastDiagnostics: (lastDiagnostics) => set({ lastDiagnostics }),
      setCachedHearingBrief: (cachedHearingBrief) => set({ cachedHearingBrief }),
      setCachedResearchSessionId: (cachedResearchSessionId) => set({ cachedResearchSessionId }),
      setWarmupStatus: (warmupStatus) => set({ warmupStatus }),
      setFilingReviewReport: (filingReviewReport) =>
        set({
          filingReviewReport,
          reviewHighlights: filingReviewReport?.findings ?? [],
          filingReviewError: null,
        }),
      setFilingReviewLoading: (filingReviewLoading) => set({ filingReviewLoading }),
      setFilingReviewError: (filingReviewError) => set({ filingReviewError }),
      setActiveReviewFindingId: (activeReviewFindingId) => set({ activeReviewFindingId }),
      setReviewHighlights: (reviewHighlights) => set({ reviewHighlights }),
      clearFilingReview: () =>
        set({
          filingReviewReport: null,
          filingReviewLoading: false,
          filingReviewError: null,
          activeReviewFindingId: null,
          reviewHighlights: [],
        }),
      startNewConversation: () =>
        set({
          currentConversationId: null,
          messages: [],
          showConversationList: false,
        }),
    }),
    {
      name: 'lexcounsel-panel',
      partialize: (s) => ({
        isOpen: s.isOpen,
        panelTab: s.panelTab,
        panelTheme: s.panelTheme,
        activeMode: s.activeMode,
        editorCounselMode: s.editorCounselMode,
        draftEditorPresentation: s.draftEditorPresentation,
      }),
    },
  ),
);

/** Imperative open for launchers outside React tree timing. */
export function openGlobalLexCounsel(options?: { mode?: LexCounselMode }): void {
  const store = useLexCounselStore.getState();
  if (options?.mode) {
    store.setActiveMode(options.mode);
  }
  store.openPanel();
}

export function applyDraftChange(change: PendingDraftChange): boolean {
  if (draftApplyHandler?.(change)) return true;
  window.dispatchEvent(
    new CustomEvent('lexcounsel-apply-draft-change', { detail: change }),
  );
  return false;
}

export function discardDraftChange(change: PendingDraftChange): void {
  window.dispatchEvent(
    new CustomEvent('lexcounsel-discard-draft-change', { detail: change }),
  );
}

export function insertDraftChangeAtCursor(change: PendingDraftChange): void {
  window.dispatchEvent(
    new CustomEvent('lexcounsel-insert-at-cursor', { detail: change }),
  );
}

type DraftApplyHandler = (change: PendingDraftChange) => boolean;

let draftApplyHandler: DraftApplyHandler | null = null;

/** LexDraftEditor registers a direct apply path (more reliable than events alone). */
export function registerLexCounselDraftApplyHandler(handler: DraftApplyHandler | null): void {
  draftApplyHandler = handler;
}

export function goToPendingDraftChange(): void {
  window.dispatchEvent(new CustomEvent('lexcounsel-go-to-pending-change'));
}

export function goToReviewFinding(finding: FilingReviewFinding): void {
  useLexCounselStore.getState().setActiveReviewFindingId(finding.id);
  window.dispatchEvent(
    new CustomEvent('lexcounsel-go-to-review-finding', { detail: finding }),
  );
}

export function runFilingReviewFindingAction(detail: {
  finding: FilingReviewFinding;
  action: import('../lib/filingReviewTypes').ReviewAction;
}): void {
  window.dispatchEvent(
    new CustomEvent('lexcounsel-review-finding-action', { detail }),
  );
}

export function pendingChangeForActiveTab(
  change: PendingDraftChange | null,
  activeTabId?: string | null,
): PendingDraftChange | null {
  if (!change) return null;
  if (!change.tabId || !activeTabId) return change;
  return change.tabId === activeTabId ? change : null;
}

export function registerLexCounselWorkspace(snapshot: Partial<LexCounselWorkspaceSnapshot>): void {
  useLexCounselStore.getState().setWorkspaceSnapshot(snapshot);
}

export function clearWorkspaceSelectionSnapshot(): void {
  registerLexCounselWorkspace({
    selectedText: undefined,
    selectedHtml: undefined,
    selectionFrom: undefined,
    selectionTo: undefined,
  });
}
